import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const EXPRESS_BUTTON_SELECTOR = '[data-express-add-to-cart]';
const EXPRESS_SCROLL_SELECTOR = '[data-express-scroll]';

if (!window.__vyseraExpressAddToCartInitialized) {
  window.__vyseraExpressAddToCartInitialized = true;

  const setStatus = (button, message) => {
    const status = button.closest('[data-express-purchase]')?.querySelector('[data-express-status]');
    if (status) status.textContent = message;
  };

  const openCartDrawer = async () => {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) return;

    await customElements.whenDefined('theme-drawer');
    if (typeof drawer.open === 'function' && !drawer.hasAttribute('open')) drawer.open();
  };

  const addToCart = async (button) => {
    if (button.dataset.expressLoading === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) {
      setStatus(button, 'Dieses Produkt ist derzeit nicht verfügbar.');
      return;
    }

    const quantity = Math.max(Number(button.dataset.quantity) || 1, 1);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [...cartItemsComponents]
      .map((component) => component instanceof HTMLElement && component.dataset.sectionId)
      .filter(Boolean);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    button.dataset.expressLoading = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setStatus(button, 'Wird in den Warenkorb gelegt …');

    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferredEventPromise.promise,
      })
    );

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', String(quantity));
    if (sectionIds.length > 0) {
      formData.append('sections', [...new Set(sectionIds)].join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const requestConfig = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Accept: 'application/json',
        },
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        throw new Error(responseData.message || 'Das Produkt konnte nicht hinzugefügt werden.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('Der Warenkorb konnte nicht aktualisiert werden.');

      const cart = await cartResponse.json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: responseData.sections,
          itemCount: cart.item_count,
          source: 'express-add-to-cart',
          sourceId: button.id,
          productId: button.dataset.productId,
          didError: false,
        },
      });

      setStatus(button, 'ErgoDuo wurde hinzugefügt.');
      await openCartDrawer();
    } catch (error) {
      deferredEventPromise.reject(error);
      button.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Das Produkt konnte nicht hinzugefügt werden.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      setStatus(button, 'Das Hinzufügen ist gerade nicht möglich. Bitte erneut versuchen.');
    } finally {
      button.dataset.expressLoading = 'false';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(EXPRESS_BUTTON_SELECTOR);
    if (button instanceof HTMLButtonElement) {
      event.preventDefault();
      addToCart(button);
      return;
    }

    const scrollLink = target?.closest(EXPRESS_SCROLL_SELECTOR);
    if (!(scrollLink instanceof HTMLAnchorElement)) return;

    const targetSelector = scrollLink.getAttribute('href') || '#buy-box';
    const destination = document.querySelector(targetSelector);
    if (!destination) return;

    event.preventDefault();
    destination.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
